import { onScopeDispose } from "vue";
import { useRouter } from "vue-router";

import type { Router } from "vue-router";

import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useSolutionsStore } from "@/store/solutions.store";
import { clearLicencesCache } from "@/utils/licenceApi";
import {
  TITAN_REMOTE_COMPANY_PRODUCTS_CHANGE_EVENT,
  isEmbedded,
  type CompanyProductsChange,
} from "@/remote/contract";
import { currentScope, routeScope, type OrgRef } from "@/remote/companyScope";

// Host -> titan: the host's own products panel added or removed a Cybersecurity product. Titan
// caches a company's licences (solutions store, licenceApi, licence store), so without this the
// nav and the product landing kept the old set until the next cold load.

export const isCompanyProductsChange = (
  value: unknown,
): value is CompanyProductsChange => {
  const change = value as CompanyProductsChange | null;
  return (
    typeof change?.organisationUuid === "string" &&
    change.organisationUuid !== "" &&
    (change.platform === "thq" || change.platform === "red")
  );
};

const sameUuid = (left: string, right: string): boolean =>
  left.toLowerCase() === right.toLowerCase();

/** The route first: embedded, a host-driven switch moves the URL before the store catches up. */
const scopeOn = (router: Router, uuid: string): OrgRef | null => {
  const route = routeScope(router);
  if (route && sameUuid(route.uuid, uuid)) return route;
  const current = currentScope();
  return current && sameUuid(current.uuid, uuid) ? current : null;
};

/** Not `resetState()`: emptying the store first flashes the "No products added" placeholder. */
const refreshProducts = async (scope: OrgRef): Promise<void> => {
  clearLicencesCache();
  useLicenceStore().clearLicenceCache();
  // Both layouts compute their nav and gate their product routes from the solutions store, and
  // the shell's Licenses view provisions a partner the same way it does a customer.
  await useSolutionsStore().getSolutions({ uuid: scope.uuid });
};

/** Refetches the products of the company titan is on when the host changes them. */
export function useCompanyProductsSync(): void {
  if (!isEmbedded()) return;

  const router = useRouter();

  const onChange = (event: Event) => {
    const change = (event as CustomEvent<unknown>).detail;
    if (!isCompanyProductsChange(change) || change.platform !== "thq") return;
    const scope = scopeOn(router, change.organisationUuid);
    if (!scope) return;
    refreshProducts(scope).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error(
        `[titan] could not refresh the products of ${scope.type} ${scope.uuid} after the host ` +
          "changed them — the nav may show the previous set until the next load.",
        error,
      );
    });
  };

  document.addEventListener(
    TITAN_REMOTE_COMPANY_PRODUCTS_CHANGE_EVENT,
    onChange,
  );
  onScopeDispose(() =>
    document.removeEventListener(
      TITAN_REMOTE_COMPANY_PRODUCTS_CHANGE_EVENT,
      onChange,
    ),
  );
}

export default useCompanyProductsSync;
